import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Layout, LaidNode, LaidEdge, hasChildren } from "./model";
import { COMPANIES, PERSONS, CATEGORIES, rupiahShort } from "./data";
import { Flag } from "./model";

interface Props {
  layout: Layout;
  step: number;
  catColors: Record<string, string>;
  companyCat: Record<string, string>;
  flags: Flag[];
  selectedId: string | null;
  onSelect: (kind: "person" | "company", id: string) => void;
  onDeselect: () => void;
  search: string;
  showOfficers: boolean;
  collapsed: Set<string>;
  onToggleCollapse: (id: string) => void;
  uboSet: Set<string>;
  fitSignal: number;
  hiddenOwners: Set<string>;
  setHiddenOwners: React.Dispatch<React.SetStateAction<Set<string>>>;
  pinnedId: string | null;
  setPinnedId: (id: string | null) => void;
  onExportChain: (kind: "png" | "pdf", nodeId: string) => void;
  catHighlight: string | null;
  setCatHighlight: (c: string | null) => void;
}

function truncate(text: string, maxWidth: number, fontSize: number, bold = false): string {
  const cw = fontSize * (bold ? 0.58 : 0.53);
  const max = Math.floor(maxWidth / cw);
  if (text.length <= max) return text;
  return text.slice(0, Math.max(1, max - 1)) + "…";
}

// Cari ukuran font agar teks MUAT penuh (tanpa dipotong), turun sampai min.
function fitFont(text: string, maxWidth: number, base: number, min: number, bold = false): number {
  let fs = base;
  while (fs > min) {
    const cw = fs * (bold ? 0.58 : 0.53);
    if (text.length * cw <= maxWidth) break;
    fs -= 0.5;
  }
  return fs;
}

function companyFlagLevelLocal(flags: Flag[], id: string): "red" | "yellow" | null {
  let has: "red" | "yellow" | null = null;
  for (const f of flags) {
    if (f.companyId !== id) continue;
    if (f.sev === "red") return "red";
    has = "yellow";
  }
  return has;
}

export default function Canvas(props: Props) {
  const { layout, catColors, companyCat, flags, selectedId, onSelect, onDeselect,
    search, showOfficers, collapsed, onToggleCollapse, uboSet, fitSignal, hiddenOwners, setHiddenOwners,
    pinnedId, setPinnedId, onExportChain, catHighlight, setCatHighlight } = props;
  const wrapRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ k: 1, tx: 0, ty: 0 });
  const pan = useRef<{ active: boolean; moved: boolean; x: number; y: number }>({ active: false, moved: false, x: 0, y: 0 });

  const fit = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const vw = el.clientWidth, vh = el.clientHeight;
    const { minX, minY, maxX, maxY } = layout.bbox;
    const pad = 70;
    const cw = maxX - minX + pad * 2;
    const ch = maxY - minY + pad * 2;
    let k = Math.min(vw / cw, vh / ch);
    k = Math.max(0.2, Math.min(3, k));
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    setView({ k, tx: vw / 2 - k * cx, ty: vh / 2 - k * cy });
  }, [layout]);

  useLayoutEffect(() => { fit(); /* eslint-disable-next-line */ }, [fitSignal]);
  useEffect(() => {
    const onR = () => fit();
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, [fit]);

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const el = wrapRef.current!;
    const rect = el.getBoundingClientRect();
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top;
    setView((v) => {
      const factor = Math.exp(-e.deltaY * 0.0015);
      let nk = Math.max(0.2, Math.min(3, v.k * factor));
      const wx = (cx - v.tx) / v.k, wy = (cy - v.ty) / v.k;
      return { k: nk, tx: cx - wx * nk, ty: cy - wy * nk };
    });
  };

  const onPointerDown = (e: React.PointerEvent) => {
    pan.current = { active: true, moved: false, x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!pan.current.active) return;
    const dx = e.clientX - pan.current.x, dy = e.clientY - pan.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) pan.current.moved = true;
    pan.current.x = e.clientX; pan.current.y = e.clientY;
    setView((v) => ({ ...v, tx: v.tx + dx, ty: v.ty + dy }));
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const wasMoved = pan.current.moved;
    pan.current.active = false;
    if (!wasMoved && (e.target as HTMLElement).tagName === "rect" && (e.target as HTMLElement).getAttribute("data-bg") === "1") {
      onDeselect();
    }
  };

  const zoomAround = (factor: number) => {
    const el = wrapRef.current;
    if (!el) return;
    const cx = el.clientWidth / 2, cy = el.clientHeight / 2;
    setView((v) => {
      const nk = Math.max(0.2, Math.min(3, v.k * factor));
      const wx = (cx - v.tx) / v.k, wy = (cy - v.ty) / v.k;
      return { k: nk, tx: cx - wx * nk, ty: cy - wy * nk };
    });
  };

  const q = search.trim().toLowerCase();
  const matched = (id: string) => {
    if (!q) return true;
    const nm = (PERSONS[id]?.name ?? COMPANIES[id]?.name ?? "").toLowerCase();
    return nm.includes(q);
  };

  const visibleEdges = useMemo(
    () => layout.edges.filter((e) => !hiddenOwners.has(e.from)),
    [layout, hiddenOwners]
  );

  // Hover-highlight: sorot relasi terkait, redupkan sisanya
  const [hover, setHover] = useState<
    null | { type: "node"; id: string } | { type: "edge"; from: string; to: string }
  >(null);
  const { hlNodes, hlEdges } = useMemo(() => {
    const all = layout.edges;
    // Prioritas: hover (preview) → selectedId (pin/kunci). Tak ada keduanya → tanpa sorotan.
    const nodes = new Set<string>();
    const edgeSet = new Set<string>();
    let startNode: string | null = null;
    if (hover) {
      if (hover.type === "edge") {
        nodes.add(hover.from); nodes.add(hover.to); edgeSet.add(hover.from + "->" + hover.to);
        return { hlNodes: nodes, hlEdges: edgeSet };
      }
      startNode = hover.id;
    } else if (pinnedId && layout.nodeMap[pinnedId]) {
      startNode = pinnedId; // TERKUNCI via tombol pin (tanpa buka panel)
    } else if (selectedId && layout.nodeMap[selectedId]) {
      startNode = selectedId; // sorotan terkunci pada kartu yang dipilih (klik)
    }
    if (!startNode) {
      // Sorotan berbasis kategori (dari legenda): terangi PT berkategori itu
      if (catHighlight) {
        const cn = new Set<string>();
        for (const n of layout.nodes) if (n.kind === "company" && (companyCat[n.id] === catHighlight)) cn.add(n.id);
        return { hlNodes: cn, hlEdges: new Set<string>() };
      }
      return { hlNodes: null as Set<string> | null, hlEdges: null as Set<string> | null };
    }
    nodes.add(startNode);
    // ke bawah: semua yang dimiliki (turunan)
    let frontier = [startNode];
    while (frontier.length) {
      const nx: string[] = [];
      for (const f of frontier) for (const e of all) if (e.from === f) {
        edgeSet.add(e.from + "->" + e.to);
        if (!nodes.has(e.to)) { nodes.add(e.to); nx.push(e.to); }
      }
      frontier = nx;
    }
    // ke atas: semua pemilik (leluhur)
    frontier = [startNode];
    while (frontier.length) {
      const nx: string[] = [];
      for (const t of frontier) for (const e of all) if (e.to === t) {
        edgeSet.add(e.from + "->" + e.to);
        if (!nodes.has(e.from)) { nodes.add(e.from); nx.push(e.from); }
      }
      frontier = nx;
    }
    return { hlNodes: nodes, hlEdges: edgeSet };
  }, [hover, layout, selectedId, pinnedId, catHighlight, companyCat]);

  const dimNode = (id: string) => (q ? !matched(id) : false) || (hlNodes ? !hlNodes.has(id) : false);
  const dimEdge = (e: LaidEdge) =>
    (q ? !(matched(e.from) && matched(e.to)) : false) || (hlEdges ? !hlEdges.has(e.from + "->" + e.to) : false);

  // Legenda pemilik: warna unik per sumber yang muncul di langkah ini
  const ownerLegend = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of layout.edges) if (!seen.has(e.from)) seen.set(e.from, e.color);
    return [...seen.entries()].map(([id, color]) => ({
      id, color, name: PERSONS[id]?.name ?? COMPANIES[id]?.short ?? id,
    }));
  }, [layout]);

  return (
    <div
      ref={wrapRef}
      className={"canvas-wrap" + (pan.current.active ? " grabbing" : "")}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={() => (pan.current.active = false)}
      data-testid="canvas"
    >
      <svg>
        <defs>
          <pattern id="grid24" width="24" height="24" patternUnits="userSpaceOnUse"
            patternTransform={`translate(${view.tx} ${view.ty}) scale(${view.k})`}>
            <path d="M24 0H0V24" fill="none" stroke="var(--grid)" strokeWidth={1} />
          </pattern>
        </defs>
        <rect data-bg="1" x={0} y={0} width="100%" height="100%" fill="url(#grid24)" />
        <g id="skt-scene" transform={`translate(${view.tx} ${view.ty}) scale(${view.k})`}>
          {/* 1) semua GARIS dulu (di belakang kartu) */}
          {visibleEdges.map((e, i) => (
            <EdgeLine key={`ln-${e.from}-${e.to}-${i}`} e={e} dim={dimEdge(e)}
              onEnter={() => setHover({ type: "edge", from: e.from, to: e.to })}
              onLeave={() => setHover(null)} />
          ))}
          {/* 2) kartu */}
          {layout.nodes.map((n) =>
            n.kind === "company" ? (
              <CompanyCard
                key={n.id} n={n}
                color={catColors[companyCat[n.id]] || "#8a94a3"}
                catName={(companyCat[n.id])}
                flagLevel={companyFlagLevelLocal(flags, n.id)}
                selected={selectedId === n.id}
                matched={matched(n.id)}
                dim={dimNode(n.id)}
                showOfficers={showOfficers}
                searchActive={!!q}
                collapsed={collapsed.has(n.id)}
                hasKids={hasChildren(n.id, props.step)}
                onSelect={() => { if (!pan.current.moved) onSelect("company", n.id); }}
                onToggleCollapse={() => onToggleCollapse(n.id)}
                onEnter={() => setHover({ type: "node", id: n.id })}
                onLeave={() => setHover(null)}
                showPin={(hover?.type === "node" && hover.id === n.id) || pinnedId === n.id}
                pinned={pinnedId === n.id}
                onPin={() => setPinnedId(pinnedId === n.id ? null : n.id)}
              />
            ) : (
              <PersonCard
                key={n.id} n={n}
                ubo={uboSet.has(n.id)}
                selected={selectedId === n.id}
                dim={dimNode(n.id)}
                matched={matched(n.id)}
                searchActive={!!q}
                onSelect={() => { if (!pan.current.moved) onSelect("person", n.id); }}
                onEnter={() => setHover({ type: "node", id: n.id })}
                onLeave={() => setHover(null)}
                showPin={(hover?.type === "node" && hover.id === n.id) || pinnedId === n.id}
                pinned={pinnedId === n.id}
                onPin={() => setPinnedId(pinnedId === n.id ? null : n.id)}
              />
            )
          )}
          {/* 3) semua LABEL persen paling atas → tak pernah tertembus garis */}
          {visibleEdges.map((e, i) => (
            <EdgeLabel key={`lb-${e.from}-${e.to}-${i}`} e={e} dim={dimEdge(e)}
              onEnter={() => setHover({ type: "edge", from: e.from, to: e.to })}
              onLeave={() => setHover(null)} />
          ))}
        </g>
      </svg>
      {(() => {
        const activeId = pinnedId ?? selectedId;
        if (!activeId || !layout.nodeMap[activeId]) return null;
        return (
          <div className="pin-hint" data-testid="pin-hint">
            <span>Sorotan terkunci: <b>{PERSONS[activeId]?.name ?? COMPANIES[activeId]?.short ?? activeId}</b></span>
            <button className="pin-release" data-testid="pin-export-png" onClick={() => onExportChain("png", activeId)} title="Ekspor rantai ini (PNG)">PNG</button>
            <button className="pin-release" data-testid="pin-export-pdf" onClick={() => onExportChain("pdf", activeId)} title="Ekspor rantai ini (PDF)">PDF</button>
            <button className="pin-release" data-testid="pin-release" onClick={() => { setPinnedId(null); onDeselect(); }}>Lepas</button>
          </div>
        );
      })()}
      {catHighlight && !pinnedId && !selectedId && (
        <div className="pin-hint" data-testid="cat-hint">
          <span>Sorotan kategori: <b>{catLabel(catHighlight)}</b></span>
          <button className="pin-release" data-testid="cat-release" onClick={() => setCatHighlight(null)}>Lepas</button>
        </div>
      )}
      <div className="zoom-ctrl">
        <button className="btn" title="Perbesar" data-testid="zoom-in" onClick={() => zoomAround(1.2)}>+</button>
        <button className="btn" title="Perkecil" data-testid="zoom-out" onClick={() => zoomAround(1 / 1.2)}>−</button>
        <button className="btn" title="Paskan" data-testid="zoom-fit" onClick={fit} style={{ fontSize: 12 }}>⤢</button>
      </div>
      {ownerLegend.length > 0 && (
        <div className="owner-legend" data-testid="owner-legend">
          <div className="owner-legend-head">
            <span className="owner-legend-title">Warna = pemilik</span>
            {hiddenOwners.size > 0 && (
              <button className="owner-legend-all" data-testid="owner-show-all"
                onClick={() => setHiddenOwners(new Set())}>Tampilkan semua</button>
            )}
          </div>
          {ownerLegend.map((o) => {
            const on = !hiddenOwners.has(o.id);
            return (
              <label className={"owner-legend-row" + (on ? "" : " off")} key={o.id} data-testid={`owner-legend-${o.id}`}>
                <input type="checkbox" checked={on} data-testid={`owner-toggle-${o.id}`}
                  onChange={() => setHiddenOwners((prev) => {
                    const next = new Set(prev);
                    if (next.has(o.id)) next.delete(o.id); else next.add(o.id);
                    return next;
                  })} />
                <span className="owner-swatch" style={{ background: o.color }} />
                <span className="owner-name" data-testid={`owner-highlight-${o.id}`}
                  onClick={(e) => { e.preventDefault(); setCatHighlight(null); setPinnedId(pinnedId === o.id ? null : o.id); }}
                  style={{ cursor: "pointer" }} title="Klik untuk menyorot rantai pemilik ini">{o.name}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EdgeLine({ e, dim, onEnter, onLeave }: { e: LaidEdge; dim: boolean; onEnter: () => void; onLeave: () => void }) {
  const d = e.path;
  const sw = e.small ? 1.3 : 2.1;
  return (
    <g opacity={dim ? 0.12 : 1} data-owner={e.from} data-edge={e.from + "->" + e.to} onMouseEnter={onEnter} onMouseLeave={onLeave} style={{ cursor: "pointer" }}>
      {/* halo: garis tampak "melintas di atas" garis lain saat bersilangan */}
      <path d={d} fill="none" stroke="var(--panel)" strokeWidth={sw + 3.4}
        strokeLinejoin="round" strokeLinecap="round" opacity={0.92} />
      <path d={d} fill="none" stroke={e.color} strokeWidth={sw}
        strokeDasharray={e.small ? "4 3" : undefined} strokeLinejoin="round" strokeLinecap="round" />
      <polygon points={`${e.ex - 5.5},${e.ey - 8} ${e.ex + 5.5},${e.ey - 8} ${e.ex},${e.ey}`} fill={e.color} />
    </g>
  );
}

function EdgeLabel({ e, dim, onEnter, onLeave }: { e: LaidEdge; dim: boolean; onEnter: () => void; onLeave: () => void }) {
  const pillW = e.label.length * 7 + 12;
  return (
    <g opacity={dim ? 0.12 : 1} data-owner={e.from} data-edge={e.from + "->" + e.to} onMouseEnter={onEnter} onMouseLeave={onLeave} style={{ cursor: "pointer" }}>
      <rect x={e.lx - pillW / 2} y={e.ly - 9.5} width={pillW} height={19} rx={9.5}
        fill="var(--panel)" stroke={e.color} strokeWidth={1} />
      <text x={e.lx} y={e.ly + 4} textAnchor="middle" fontSize={11}
        className="tnum" fill={e.color} style={{ fontFamily: "Public Sans", fontWeight: 700 }}>{e.label}</text>
    </g>
  );
}

function CompanyCard(props: {
  n: LaidNode; color: string; catName: string; flagLevel: "red" | "yellow" | null;
  selected: boolean; matched: boolean; dim: boolean; showOfficers: boolean; searchActive: boolean;
  collapsed: boolean; hasKids: boolean; onSelect: () => void; onToggleCollapse: () => void;
  onEnter: () => void; onLeave: () => void;
  showPin: boolean; pinned: boolean; onPin: () => void;
}) {
  const { n, color, flagLevel, selected, matched, dim, showOfficers, collapsed, hasKids, onSelect, onToggleCollapse, onEnter, onLeave, showPin, pinned, onPin } = props;
  const v = n.version!;
  const c = COMPANIES[n.id];
  const x = n.x, y = n.y, w = n.w, h = n.h;
  const r = 10;
  const ribbonPath = `M ${x} ${y + r} Q ${x} ${y} ${x + r} ${y} H ${x + w - r} Q ${x + w} ${y} ${x + w} ${y + r} V ${y + 26} H ${x} Z`;

  const strokeW = selected ? 2 : 1;
  const cardStroke = selected ? "var(--focus)" : (props.searchActive && matched ? "var(--info)" : "var(--border)");

  let yy = y + 26 + 16;
  const modalLine = `MD ${rupiahShort(v.dasar)}   MS ${rupiahShort(v.ditempatkan)}   @${rupiahShort(v.nominal)}`;
  const kbliLine = "KBLI " + c.kbli.map((k) => k.code).join(", ");

  return (
    <g opacity={dim ? 0.28 : 1} style={{ cursor: "pointer" }} tabIndex={0} role="button"
      aria-label={c.name}
      data-testid={`card-${n.id}`} data-node={n.id}
      onClick={onSelect}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}>
      <rect x={x} y={y} width={w} height={h} rx={r} fill="var(--panel)"
        stroke={cardStroke} strokeWidth={strokeW} />
      <path d={ribbonPath} fill={color} />
      <text x={x + w / 2} y={y + 17} textAnchor="middle" fontSize={10.5} fill="#ffffff"
        style={{ fontFamily: "Public Sans", fontWeight: 600 }}>
        {truncate(catLabel(props.catName || "none"), w - 40, 10.5, true)}
      </text>

      {flagLevel && (
        <g>
          <circle cx={x + w - 16} cy={y + 13} r={8} fill="#ffffff" />
          <text x={x + w - 16} y={y + 17} textAnchor="middle" fontSize={12}
            fill={flagLevel === "red" ? "var(--red)" : "var(--yellow)"} style={{ fontWeight: 700 }}>!</text>
        </g>
      )}

      {showPin && (
        <g data-testid={`pin-${n.id}`} style={{ cursor: "pointer" }}
          onClick={(e) => { e.stopPropagation(); onPin(); }}
          onMouseEnter={onEnter}>
          <circle cx={x + 16} cy={y + 13} r={9} fill={pinned ? "var(--focus)" : "var(--panel)"} stroke="var(--focus)" strokeWidth={1.4} />
          <circle cx={x + 16} cy={y + 10.5} r={2.4} fill={pinned ? "#fff" : "var(--focus)"} />
          <rect x={x + 15} y={y + 11} width={2} height={6} fill={pinned ? "#fff" : "var(--focus)"} />
          <title>{pinned ? "Lepas kunci sorotan" : "Kunci sorotan rantai"}</title>
        </g>
      )}

      <text x={x + w / 2} y={yy} textAnchor="middle" fontSize={15} className="serif" fill="var(--ink)">
        {truncate(c.name, w - 24, 15, true)}
      </text>
      <text x={x + w / 2} y={yy + 19} textAnchor="middle" fontSize={11} fill="var(--muted)" className="tnum">
        {truncate(modalLine, w - 20, 11)}
      </text>
      <text x={x + w / 2} y={yy + 36} textAnchor="middle" fontSize={11} fill="var(--secondary)">
        {truncate(kbliLine, w - 20, 11)}
      </text>

      {showOfficers && v.officers.map((o, i) => (
        <text key={i} x={x + w / 2} y={yy + 54 + i * 15} textAnchor="middle" fontSize={11.5} fill="var(--ink)">
          {truncate(`${o[1]}: ${PERSONS[o[0]]?.name ?? o[0]}`, w - 20, 11.5)}
        </text>
      ))}
      {showOfficers && (
        <text x={x + w / 2} y={yy + 54 + v.officers.length * 15 + 13} textAnchor="middle" fontSize={10.5} fill="var(--muted)">
          {truncate([c.kedudukan, c.operasional].filter(Boolean).join(", "), w - 20, 10.5)}
        </text>
      )}
      {showOfficers && c.kpp && (
        <text x={x + w / 2} y={yy + 54 + v.officers.length * 15 + 27} textAnchor="middle"
          fontSize={fitFont(c.kpp, w - 14, 10.5, 7.5, true)} fill="var(--secondary)" style={{ fontWeight: 600 }}>
          {c.kpp}
        </text>
      )}

      {/* catatan */}
      {n.notes.map((note, i) => {
        const ny = y + h + 4 + i * 17;
        return (
          <g key={i}>
            <rect x={x + 14} y={ny} width={w - 28} height={15} rx={4} fill="var(--page)"
              stroke="var(--border)" strokeWidth={1} strokeDasharray="3 2" />
            <text x={x + 20} y={ny + 11} fontSize={10} fill="var(--secondary)">
              {truncate(note.text, w - 44, 10)}
            </text>
          </g>
        );
      })}

      {hasKids && (
        <g style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
          data-testid={`collapse-${n.id}`}>
          <circle cx={x + w / 2} cy={y + h} r={9} fill="var(--panel)" stroke="var(--border)" strokeWidth={1} />
          <text x={x + w / 2} y={y + h + 4} textAnchor="middle" fontSize={14} fill="var(--secondary)"
            style={{ fontWeight: 700 }}>{collapsed ? "+" : "−"}</text>
        </g>
      )}
    </g>
  );
}

function PersonCard(props: {
  n: LaidNode; ubo: boolean; selected: boolean; dim: boolean; matched: boolean; searchActive: boolean; onSelect: () => void;
  onEnter: () => void; onLeave: () => void;
  showPin: boolean; pinned: boolean; onPin: () => void;
}) {
  const { n, ubo, selected, dim, onSelect, onEnter, onLeave, showPin, pinned, onPin } = props;
  const x = n.x, y = n.y, w = n.w, h = n.h;
  const off = n.badgeRow ? 18 : 0;
  const p = PERSONS[n.id];
  const stroke = selected ? "var(--focus)" : (props.searchActive && props.matched ? "var(--info)" : "var(--border)");
  return (
    <g opacity={dim ? 0.28 : 1} style={{ cursor: "pointer" }} tabIndex={0} role="button" aria-label={p.name}
      data-testid={`card-${n.id}`} data-node={n.id}
      onClick={onSelect}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}>
      <rect x={x} y={y} width={w} height={h} rx={16} fill="var(--panel)"
        stroke={stroke} strokeWidth={selected ? 2 : 1} />
      {ubo && (
        <g>
          <rect x={x + w / 2 - 21} y={y + 9} width={42} height={16} rx={8} fill="var(--red)" />
          <text x={x + w / 2} y={y + 20.5} textAnchor="middle" fontSize={9.5} fill="#fff" style={{ fontWeight: 700, letterSpacing: 0.5 }}>UBO</text>
          <title>Kandidat UBO: kepemilikan efektif di atas 25%</title>
        </g>
      )}
      <text x={x + w / 2} y={y + 24 + off} textAnchor="middle" fontSize={14} className="serif" fill="var(--ink)">
        {truncate(p.name, w - 26, 14, true)}
      </text>
      <text x={x + w / 2} y={y + 40 + off} textAnchor="middle" fontSize={10} fill="var(--muted)">
        {ubo ? "kandidat UBO (>25% efektif)" : "Pemegang saham"}
      </text>
      {(n.holdings ?? []).map((hd, i) => (
        <text key={hd.companyId} x={x + w / 2} y={y + 56 + off + i * 15} textAnchor="middle"
          fontSize={10.5} fill="var(--secondary)" className="tnum">
          {truncate(`${COMPANIES[hd.companyId]?.short ?? hd.companyId}: ${hd.shares.toLocaleString("id-ID")} lbr · ${rupiahShort(hd.amount)}`, w - 18, 10.5)}
        </text>
      ))}
      {(!n.holdings || n.holdings.length === 0) && (
        <text x={x + w / 2} y={y + 56 + off} textAnchor="middle" fontSize={10.5} fill="var(--muted)">Setoran modal: —</text>
      )}
      {showPin && (
        <g data-testid={`pin-${n.id}`} style={{ cursor: "pointer" }}
          onClick={(e) => { e.stopPropagation(); onPin(); }}
          onMouseEnter={onEnter}>
          <circle cx={x + 16} cy={y + 14} r={9} fill={pinned ? "var(--focus)" : "var(--panel)"} stroke="var(--focus)" strokeWidth={1.4} />
          <circle cx={x + 16} cy={y + 11.5} r={2.4} fill={pinned ? "#fff" : "var(--focus)"} />
          <rect x={x + 15} y={y + 12} width={2} height={6} fill={pinned ? "#fff" : "var(--focus)"} />
          <title>{pinned ? "Lepas kunci sorotan" : "Kunci sorotan rantai"}</title>
        </g>
      )}
    </g>
  );
}

function catLabel(catId: string): string {
  return CATEGORIES[catId]?.name || catId;
}
